import type { DatabaseFile, NavOrderEntry, NavOrderFile } from '../types';
import {
  ROUTE_CONFIG,
  ROUTE_CONFIG_IMPORT,
  ROUTE_CURSOR_AGENT,
  ROUTE_DAILY,
  ROUTE_CHESS,
  ROUTE_EAR_TRAINING,
  ROUTE_TOOLS_ENCRYPT,
  ROUTE_NEWS,
  ROUTE_WEATHER,
} from '../constants/routes';
import { getBuildDateLabel } from '../utils/buildInfo';
import { openHttpDebugModal } from '../utils/httpDebugLog';
import { openMediaDebugModal } from '../utils/mediaDebugLog';
import { openSaveDebugModal } from '../utils/saveDebugLog';
import { displayNameFromPath } from '../utils/markdownPaths';
import { escapeAttr } from '../utils/escapeHtml';
import { dragDistanceExceeded, getDragAfterElement } from '../utils/pointerDragReorder';
import { pageIcon, renderPageIconHtml } from '../utils/pageIcon';

const DB_PREFIX = 'database/';

interface TreeNode {
  folders: Map<string, TreeNode>;
  files: string[];
}

/** Paths under database/ → tree (relative segments). */
function pathsToTree(paths: string[]): TreeNode {
  const root: TreeNode = { folders: new Map(), files: [] };
  for (const fullPath of paths) {
    const rel = fullPath.startsWith(DB_PREFIX) ? fullPath.slice(DB_PREFIX.length) : fullPath;
    const segments = rel.split('/').filter(Boolean);
    if (segments.length === 0) continue;
    const fileName = segments.pop()!;
    if (!fileName.endsWith('.md')) continue;

    let node = root;
    for (const seg of segments) {
      let next = node.folders.get(seg);
      if (!next) {
        next = { folders: new Map(), files: [] };
        node.folders.set(seg, next);
      }
      node = next;
    }
    node.files.push(fullPath);
  }
  return root;
}

type ChildItem =
  | { kind: 'file'; path: string }
  | { kind: 'dir'; dir: string; segment: string; node: TreeNode };

function mergeChildOrder(
  parentDir: string,
  files: string[],
  folderMap: Map<string, TreeNode>,
  navOrder: NavOrderFile | null
): ChildItem[] {
  const fileSet = new Set(files);
  const folderSegs = new Set(folderMap.keys());
  const usedFiles = new Set<string>();
  const usedSegs = new Set<string>();
  const result: ChildItem[] = [];

  const list = navOrder?.order[parentDir];
  if (list && list.length > 0) {
    for (const e of list) {
      if (e.kind === 'file' && fileSet.has(e.path)) {
        result.push({ kind: 'file', path: e.path });
        usedFiles.add(e.path);
      } else if (e.kind === 'dir') {
        const seg = e.dir.split('/').filter(Boolean).pop();
        if (seg && folderMap.has(seg) && e.dir === `${parentDir}/${seg}`) {
          result.push({
            kind: 'dir',
            dir: e.dir,
            segment: seg,
            node: folderMap.get(seg)!,
          });
          usedSegs.add(seg);
        }
      }
    }
  }

  for (const seg of [...folderSegs].filter(s => !usedSegs.has(s)).sort((a, b) => a.localeCompare(b))) {
    result.push({
      kind: 'dir',
      dir: `${parentDir}/${seg}`,
      segment: seg,
      node: folderMap.get(seg)!,
    });
  }

  for (const p of [...files].filter(f => !usedFiles.has(f)).sort((a, b) => a.localeCompare(b))) {
    result.push({ kind: 'file', path: p });
  }

  return result;
}

export class Sidebar {
  private readonly onSelect: (file: DatabaseFile) => void;
  private readonly onNewFileInDir: (dir: string) => void;
  private readonly onNewFolderInDir: (dir: string) => void;
  private readonly onImportMarkdownInDir: (dir: string) => void;
  private readonly onNavOrderChange: (
    next: NavOrderFile,
    fileRelocations: ReadonlyArray<{ oldPath: string; newPath: string }>
  ) => void;
  private readonly onRenameFolder: (dir: string) => void;
  private readonly onHomeClick: () => void;
  private readonly onWeatherClick: () => void;
  private readonly onNewsClick: () => void;
  private readonly onDailyUpdateClick: () => void;
  private readonly onEarTrainingClick: () => void;
  private readonly onChessClick: () => void;
  private readonly onEncryptLinkClick: () => void;
  private readonly onConfigClick: () => void;
  private readonly onCursorAgentClick: () => void;
  private toolsMenuOpen = false;
  private debugMenuOpen = false;
  private activePath = '';
  private dailyDashboardAvailable = false;
  private currentNavOrder: NavOrderFile = { version: 2, order: {} };
  private dndAbort: AbortController | null = null;
  private gestureAbort: AbortController | null = null;

  constructor(
    onSelect: (file: DatabaseFile) => void,
    onNewFileInDir: (dir: string) => void,
    onNewFolderInDir: (dir: string) => void,
    onImportMarkdownInDir: (dir: string) => void,
    onNavOrderChange: (
      next: NavOrderFile,
      fileRelocations: ReadonlyArray<{ oldPath: string; newPath: string }>
    ) => void,
    onRenameFolder: (dir: string) => void,
    onHomeClick: () => void,
    onWeatherClick: () => void,
    onNewsClick: () => void,
    onDailyUpdateClick: () => void,
    onEarTrainingClick: () => void,
    onChessClick: () => void,
    onEncryptLinkClick: () => void,
    onConfigClick: () => void,
    onCursorAgentClick: () => void
  ) {
    this.onSelect = onSelect;
    this.onNewFileInDir = onNewFileInDir;
    this.onNewFolderInDir = onNewFolderInDir;
    this.onImportMarkdownInDir = onImportMarkdownInDir;
    this.onNavOrderChange = onNavOrderChange;
    this.onRenameFolder = onRenameFolder;
    this.onHomeClick = onHomeClick;
    this.onWeatherClick = onWeatherClick;
    this.onNewsClick = onNewsClick;
    this.onDailyUpdateClick = onDailyUpdateClick;
    this.onEarTrainingClick = onEarTrainingClick;
    this.onChessClick = onChessClick;
    this.onEncryptLinkClick = onEncryptLinkClick;
    this.onConfigClick = onConfigClick;
    this.onCursorAgentClick = onCursorAgentClick;
  }

  render(container: HTMLElement, onLogout: () => void): void {
    container.innerHTML = `
      <div class="sidebar" id="sidebar-inner">
        <div class="sidebar-header">
          <button id="sidebar-toggle" class="sidebar-toggle" aria-label="Toggle menu">☰</button>
          <img class="sidebar-logo" src="/codenesium-favicon.png" alt="" width="24" height="24" decoding="async" />
          <button type="button" class="sidebar-title" id="sidebar-home-btn" aria-label="Home">Codenesium</button>
        </div>
        <div class="sidebar-controls" id="sidebar-controls">
          <div class="sidebar-search-host" id="sidebar-search-host"></div>
          <div class="sidebar-toolbar-actions">
            <button type="button" id="btn-new-root-file" class="btn-sidebar-toplevel" title="Create a new file">＋ File</button>
            <button type="button" id="btn-new-root-folder" class="btn-sidebar-toplevel" title="Create a new folder">📁 Folder</button>
            <button type="button" id="btn-import-root-md" class="btn-sidebar-toplevel" title="Import markdown into database/">📥 Import</button>
          </div>
        </div>
        <nav class="sidebar-nav" id="sidebar-nav"></nav>
        <div class="sidebar-footer">
          <div class="sidebar-footer-tools" id="sidebar-footer-tools">
            <button
              type="button"
              id="tools-menu-toggle"
              class="btn-ghost btn-sm sidebar-tools-toggle"
              aria-expanded="false"
              aria-controls="tools-menu-panel"
            >Tools ▾</button>
            <div class="tools-menu-panel hidden" id="tools-menu-panel" role="menu">
              <button type="button" class="tools-menu-item" id="nav-encrypt-link" role="menuitem">
                🔐 Encrypt link
              </button>
              <button type="button" class="tools-menu-item" id="nav-config" role="menuitem">
                ⚙ Config
              </button>
            </div>
            <button
              type="button"
              id="debug-menu-toggle"
              class="btn-ghost btn-sm sidebar-tools-toggle"
              aria-expanded="false"
              aria-controls="debug-menu-panel"
            >Debug ▾</button>
            <div class="tools-menu-panel hidden" id="debug-menu-panel" role="menu">
              <button type="button" class="tools-menu-item" id="debug-http" role="menuitem">
                HTTP log
              </button>
              <button type="button" class="tools-menu-item" id="debug-media" role="menuitem">
                Media log
              </button>
              <button type="button" class="tools-menu-item" id="debug-save" role="menuitem">
                Save log
              </button>
            </div>
          </div>
          <p class="sidebar-built">${escapeAttr(getBuildDateLabel())}</p>
          <div class="sidebar-footer-actions">
            <button type="button" id="logout-btn" class="btn-ghost btn-sm">⚙ Sign out</button>
          </div>
        </div>
      </div>
    `;

    container.querySelector('#logout-btn')!
      .addEventListener('click', onLogout);

    container.querySelector('#btn-new-root-file')!
      .addEventListener('click', () => this.onNewFileInDir('database'));

    container.querySelector('#sidebar-home-btn')!
      .addEventListener('click', () => {
        this.collapseSidebarAfterNav(container);
        this.onHomeClick();
      });

    container.querySelector('#btn-new-root-folder')!
      .addEventListener('click', () => this.onNewFolderInDir('database'));

    container.querySelector('#btn-import-root-md')!
      .addEventListener('click', () => this.onImportMarkdownInDir('database'));

    container.querySelector('#sidebar-toggle')!
      .addEventListener('click', () => {
        const wrapper = container.closest('.sidebar-wrapper') as HTMLElement;
        const collapsed = wrapper.classList.toggle('collapsed');
        localStorage.setItem('sidebar_collapsed', String(collapsed));
      });

    const toolsToggle = container.querySelector<HTMLButtonElement>('#tools-menu-toggle')!;
    const toolsPanel = container.querySelector<HTMLElement>('#tools-menu-panel')!;
    const debugToggle = container.querySelector<HTMLButtonElement>('#debug-menu-toggle')!;
    const debugPanel = container.querySelector<HTMLElement>('#debug-menu-panel')!;
    const setToolsOpen = (open: boolean) => {
      this.toolsMenuOpen = open;
      toolsPanel.classList.toggle('hidden', !open);
      toolsToggle.setAttribute('aria-expanded', String(open));
      toolsToggle.classList.toggle('active', open);
    };
    const setDebugOpen = (open: boolean) => {
      this.debugMenuOpen = open;
      debugPanel.classList.toggle('hidden', !open);
      debugToggle.setAttribute('aria-expanded', String(open));
      debugToggle.classList.toggle('active', open);
    };
    toolsToggle.addEventListener('click', e => {
      e.stopPropagation();
      const next = !this.toolsMenuOpen;
      if (next) setDebugOpen(false);
      setToolsOpen(next);
    });
    debugToggle.addEventListener('click', e => {
      e.stopPropagation();
      const next = !this.debugMenuOpen;
      if (next) setToolsOpen(false);
      setDebugOpen(next);
    });
    document.addEventListener('click', e => {
      const toolsRoot = container.querySelector('#sidebar-footer-tools');
      if (!toolsRoot?.contains(e.target as Node)) {
        if (this.toolsMenuOpen) setToolsOpen(false);
        if (this.debugMenuOpen) setDebugOpen(false);
      }
    });
    container.querySelector('#debug-http')!.addEventListener('click', () => {
      setDebugOpen(false);
      openHttpDebugModal();
    });
    container.querySelector('#debug-media')!.addEventListener('click', () => {
      setDebugOpen(false);
      openMediaDebugModal();
    });
    container.querySelector('#debug-save')!.addEventListener('click', () => {
      setDebugOpen(false);
      openSaveDebugModal();
    });
    container.querySelector('#nav-encrypt-link')!.addEventListener('click', () => {
      setToolsOpen(false);
      this.activePath = ROUTE_TOOLS_ENCRYPT;
      this.collapseSidebarAfterNav(container);
      this.onEncryptLinkClick();
    });
    container.querySelector('#nav-config')!.addEventListener('click', () => {
      setToolsOpen(false);
      this.activePath = ROUTE_CONFIG;
      this.collapseSidebarAfterNav(container);
      this.onConfigClick();
    });

    if (localStorage.getItem('sidebar_collapsed') === 'true') {
      const wrapper = container.closest('.sidebar-wrapper') as HTMLElement;
      wrapper?.classList.add('collapsed');
    }
  }

  /** On small screens, tuck the sidebar away after navigating to a leaf destination. */
  private collapseSidebarAfterNav(container: HTMLElement): void {
    if (!window.matchMedia('(max-width: 600px)').matches) return;
    const wrapper = container.closest('.sidebar-wrapper') as HTMLElement | null;
    if (!wrapper) return;
    wrapper.classList.add('collapsed');
    localStorage.setItem('sidebar_collapsed', 'true');
  }

  setActivePath(path: string): void {
    this.activePath = path;
  }

  setDailyDashboardAvailable(available: boolean): void {
    this.dailyDashboardAvailable = available;
  }

  getActivePath(): string {
    return this.activePath;
  }

  updateNav(
    container: HTMLElement,
    allPaths: string[],
    loadedFiles: DatabaseFile[],
    navOrder: NavOrderFile
  ): void {
    const nav = container.querySelector<HTMLElement>('#sidebar-nav');
    if (!nav) return;

    this.currentNavOrder = navOrder;
    const tree = pathsToTree(allPaths);
    const inner = this.renderTree('database', tree, loadedFiles, navOrder);
    const cursorAgentActive = this.activePath === ROUTE_CURSOR_AGENT ? 'active' : '';
    const weatherActive = this.activePath === ROUTE_WEATHER ? 'active' : '';
    const newsActive = this.activePath === ROUTE_NEWS ? 'active' : '';
    const dailyActive = this.activePath === ROUTE_DAILY ? 'active' : '';
    const earActive = this.activePath === ROUTE_EAR_TRAINING ? 'active' : '';
    const chessActive = this.activePath === ROUTE_CHESS ? 'active' : '';
    const specialLinks = `<div class="nav-special-links">
        <button type="button" class="nav-item nav-special-item ${cursorAgentActive}" id="nav-cursor-agent">
          🤖 <span>AI</span>
        </button>
        <button type="button" class="nav-item nav-special-item ${weatherActive}" id="nav-weather">
          🌤 <span>Weather</span>
        </button>
        <button type="button" class="nav-item nav-special-item ${newsActive}" id="nav-news">
          📰 <span>News</span>
        </button>
        ${
          this.dailyDashboardAvailable
            ? `<button type="button" class="nav-item nav-special-item ${dailyActive}" id="nav-daily-update">
          📬 <span>Daily Update</span>
        </button>`
            : ''
        }
        <button type="button" class="nav-item nav-special-item ${earActive}" id="nav-ear-training">
          🎸 <span>Ear Training</span>
        </button>
        <button type="button" class="nav-item nav-special-item ${chessActive}" id="nav-chess">
          ♟ <span>Chess</span>
        </button>
      </div>`;
    nav.innerHTML =
      specialLinks +
      (inner
        ? `<div class="nav-drop-zone" data-parent-dir="database">${inner}</div>`
        : `<p class="sidebar-empty">No files under database/ yet.</p>`);

    const configActive =
      this.activePath === ROUTE_CONFIG || this.activePath === ROUTE_CONFIG_IMPORT;
    const toolsActive = this.activePath === ROUTE_TOOLS_ENCRYPT || configActive;
    container.querySelector('#nav-encrypt-link')?.classList.toggle(
      'active',
      this.activePath === ROUTE_TOOLS_ENCRYPT
    );
    container.querySelector('#nav-config')?.classList.toggle('active', configActive);
    container.querySelector('.sidebar-tools-toggle')?.classList.toggle('active', toolsActive);

    const homeBtn = container.querySelector<HTMLButtonElement>('#sidebar-home-btn');
    homeBtn?.classList.toggle('active', this.activePath === ROUTE_CURSOR_AGENT);
    if (homeBtn) {
      homeBtn.setAttribute('aria-label', 'Home — AI Agent');
    }

    nav.querySelector<HTMLButtonElement>('#nav-cursor-agent')?.addEventListener('click', () => {
      this.activePath = ROUTE_CURSOR_AGENT;
      this.updateNav(container, allPaths, loadedFiles, navOrder);
      this.collapseSidebarAfterNav(container);
      this.onCursorAgentClick();
    });

    nav.querySelector<HTMLButtonElement>('#nav-weather')?.addEventListener('click', () => {
      this.activePath = ROUTE_WEATHER;
      this.updateNav(container, allPaths, loadedFiles, navOrder);
      this.collapseSidebarAfterNav(container);
      this.onWeatherClick();
    });

    nav.querySelector<HTMLButtonElement>('#nav-news')?.addEventListener('click', () => {
      this.activePath = ROUTE_NEWS;
      this.updateNav(container, allPaths, loadedFiles, navOrder);
      this.collapseSidebarAfterNav(container);
      this.onNewsClick();
    });

    nav.querySelector<HTMLButtonElement>('#nav-daily-update')?.addEventListener('click', () => {
      this.activePath = ROUTE_DAILY;
      this.updateNav(container, allPaths, loadedFiles, navOrder);
      this.collapseSidebarAfterNav(container);
      this.onDailyUpdateClick();
    });

    nav.querySelector<HTMLButtonElement>('#nav-ear-training')?.addEventListener('click', () => {
      this.activePath = ROUTE_EAR_TRAINING;
      this.updateNav(container, allPaths, loadedFiles, navOrder);
      this.collapseSidebarAfterNav(container);
      this.onEarTrainingClick();
    });

    nav.querySelector<HTMLButtonElement>('#nav-chess')?.addEventListener('click', () => {
      this.activePath = ROUTE_CHESS;
      this.updateNav(container, allPaths, loadedFiles, navOrder);
      this.collapseSidebarAfterNav(container);
      this.onChessClick();
    });

    nav.querySelectorAll<HTMLButtonElement>('.btn-new-file').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        e.preventDefault();
        const dir = btn.dataset['dir'];
        if (dir) this.onNewFileInDir(dir);
      });
    });

    nav.querySelectorAll<HTMLButtonElement>('.btn-new-folder').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        e.preventDefault();
        const dir = btn.dataset['dir'];
        if (dir) this.onNewFolderInDir(dir);
      });
    });

    nav.querySelectorAll<HTMLButtonElement>('.btn-rename-folder').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        e.preventDefault();
        const dir = btn.dataset['dir'];
        if (dir) this.onRenameFolder(dir);
      });
    });

    nav.querySelectorAll<HTMLButtonElement>('.nav-file').forEach(btn => {
      btn.addEventListener('click', () => {
        const path = btn.dataset['path'];
        if (!path) return;
        const loaded = loadedFiles.find(f => f.path === path);
        this.activePath = path;
        this.updateNav(container, allPaths, loadedFiles, navOrder);
        this.collapseSidebarAfterNav(container);
        if (loaded) {
          this.onSelect(loaded);
        } else {
          this.onSelect({
            path,
            name: displayNameFromPath(path),
            sha: '',
            sections: [],
            orphanedImagePaths: [],
            orphanedAudioPaths: [],
            orphanedVideoPaths: [],
          });
        }
      });
    });

    this.wirePointerReorder(nav);
  }

  private wirePointerReorder(nav: HTMLElement): void {
    this.dndAbort?.abort();
    this.gestureAbort?.abort();
    this.dndAbort = new AbortController();
    const { signal } = this.dndAbort;

    nav.addEventListener(
      'pointerdown',
      e => {
        if (e.button !== 0) return;
        if (!(e.target as HTMLElement).closest('.nav-drag-handle')) return;
        e.stopPropagation();
        const dr = (e.target as HTMLElement | null)?.closest('.nav-draggable') as HTMLElement | null;
        if (!dr || !nav.contains(dr)) return;

        const zone = dr.parentElement as HTMLElement | null;
        if (!zone?.classList.contains('nav-drop-zone')) return;

        const dragFile = dr.dataset['kind'] === 'file';

        const pid = e.pointerId;
        const el = dr;
        const startX = e.clientX;
        const startY = e.clientY;
        let active = false;

        this.gestureAbort?.abort();
        this.gestureAbort = new AbortController();
        const g = this.gestureAbort.signal;

        const finish = (ev: PointerEvent) => {
          if (ev.pointerId !== pid) return;
          if (active) {
            try {
              el.releasePointerCapture(pid);
            } catch {
              /* ignore */
            }
            nav.classList.remove('nav-reorder-active');
            el.classList.remove('nav-dragging');
            const fileRelocations = collectFileRelocations(nav);
            const next = captureNavOrderFromDom(nav, this.currentNavOrder);
            this.onNavOrderChange(next, fileRelocations);
          }
          this.gestureAbort?.abort();
          this.gestureAbort = null;
        };

        const onPointerMove = (ev: PointerEvent) => {
          if (ev.pointerId !== pid) return;
          if (!active) {
            if (!dragDistanceExceeded(startX, startY, ev.clientX, ev.clientY)) return;
            active = true;
            nav.classList.add('nav-reorder-active');
            el.classList.add('nav-dragging');
            try {
              el.setPointerCapture(pid);
            } catch {
              /* ignore */
            }
          }
          ev.preventDefault();
          let targetZone = zone;
          if (dragFile) {
            const hit = findNavDropZoneAt(nav, ev.clientX, ev.clientY);
            if (hit) targetZone = hit;
            else {
              const p = el.parentElement;
              if (p?.classList.contains('nav-drop-zone')) targetZone = p;
            }
          }
          const after = getDragAfterElement<HTMLElement>(
            targetZone,
            ev.clientY,
            ':scope > .nav-draggable:not(.nav-dragging)'
          );
          if (after == null) targetZone.appendChild(el);
          else targetZone.insertBefore(el, after);
        };

        window.addEventListener('pointermove', onPointerMove, { signal: g, passive: false });
        window.addEventListener('pointerup', finish, { signal: g });
        window.addEventListener('pointercancel', finish, { signal: g });
      },
      { signal }
    );
  }

  private renderTree(
    dirPrefix: string,
    node: TreeNode,
    loadedFiles: DatabaseFile[],
    navOrder: NavOrderFile
  ): string {
    const children = mergeChildOrder(dirPrefix, node.files, node.folders, navOrder);
    const parts = children.map(item => {
      if (item.kind === 'file') {
        const fullPath = item.path;
        const loaded = loadedFiles.find(f => f.path === fullPath);
        const name = loaded?.name ?? displayNameFromPath(fullPath);
        const badge = loaded ? this.pendingBadge(loaded) : '';
        const active = this.activePath === fullPath ? 'active' : '';
        return `<div class="nav-draggable" data-kind="file" data-path="${escapeAttr(fullPath)}">
          <button type="button" class="nav-drag-handle" aria-label="Drag to reorder or move to another folder" title="Drag to reorder or move to another folder">☰</button>
          <button type="button" class="nav-item nav-file nav-sub-item ${active}" data-path="${escapeAttr(fullPath)}">${renderPageIconHtml(pageIcon(fullPath, navOrder))} <span>${escapeAttr(name)}</span> ${badge}</button>
        </div>`;
      }
      const child = item.node;
      const childDir = item.dir;
      const shouldOpen =
        child.files.includes(this.activePath) || this.subtreeHasActive(child, childDir);
      const inner = this.renderTree(childDir, child, loadedFiles, navOrder);
      return `<div class="nav-draggable" data-kind="dir" data-dir="${escapeAttr(childDir)}">
        <button type="button" class="nav-drag-handle" aria-label="Drag to reorder" title="Drag to reorder">☰</button>
        <details class="nav-group" ${shouldOpen ? 'open' : ''}>
          <summary class="nav-item nav-group-header">
            <span class="nav-folder-summary-main">📂 <span>${escapeAttr(item.segment)}</span></span>
            <button type="button" class="btn-rename-folder" data-dir="${escapeAttr(childDir)}" title="Change folder name">✏️</button>
            <button type="button" class="btn-new-folder" data-dir="${escapeAttr(childDir)}" title="New subfolder">📁</button>
            <button type="button" class="btn-new-file" data-dir="${escapeAttr(childDir)}" title="New file in this folder">＋</button>
          </summary>
          <div class="nav-sub">
            <div class="nav-drop-zone" data-parent-dir="${escapeAttr(childDir)}">${inner}</div>
          </div>
        </details>
      </div>`;
    });

    return parts.join('');
  }

  private subtreeHasActive(node: TreeNode, dirPrefix: string): boolean {
    if (node.files.some(p => p === this.activePath)) return true;
    for (const [key, child] of node.folders) {
      const childDir = `${dirPrefix}/${key}`;
      if (this.activePath.startsWith(`${childDir}/`) || this.subtreeHasActive(child, childDir)) return true;
    }
    return false;
  }

  private pendingBadge(file: DatabaseFile): string {
    const count = file.sections.flatMap(s => s.items).filter(i => i.state === 'open').length;
    return count > 0 ? `<span class="badge">${count}</span>` : '';
  }
}

function domDepthFromNav(el: HTMLElement, nav: HTMLElement): number {
  let d = 0;
  for (let n: HTMLElement | null = el; n && n !== nav; n = n.parentElement) d++;
  return d;
}

/** Deepest `.nav-drop-zone` under the pointer (nested folder zones win over the root zone). */
function findNavDropZoneAt(nav: HTMLElement, x: number, y: number): HTMLElement | null {
  const zones = [...nav.querySelectorAll<HTMLElement>('.nav-drop-zone')];
  const hits = zones.filter(z => {
    const r = z.getBoundingClientRect();
    return y >= r.top && y <= r.bottom && x >= r.left && x <= r.right;
  });
  if (hits.length === 0) return null;
  hits.sort((a, b) => domDepthFromNav(b, nav) - domDepthFromNav(a, nav));
  return hits[0]!;
}

function collectFileRelocations(nav: HTMLElement): { oldPath: string; newPath: string }[] {
  const out: { oldPath: string; newPath: string }[] = [];
  nav.querySelectorAll<HTMLElement>('.nav-drop-zone').forEach(zone => {
    const parent = zone.dataset['parentDir'];
    if (!parent) return;
    zone.querySelectorAll<HTMLElement>(':scope > .nav-draggable[data-kind="file"]').forEach(dr => {
      const oldPath = dr.dataset['path'];
      if (!oldPath) return;
      const base = oldPath.split('/').pop()!;
      const newPath = `${parent}/${base}`;
      if (newPath !== oldPath) out.push({ oldPath, newPath });
    });
  });
  return out;
}

function captureNavOrderFromDom(nav: HTMLElement, prev: NavOrderFile): NavOrderFile {
  const order: Record<string, NavOrderEntry[]> = {};
  nav.querySelectorAll<HTMLElement>('.nav-drop-zone').forEach(zone => {
    const parent = zone.dataset['parentDir'];
    if (!parent) return;
    const entries: NavOrderEntry[] = [];
    zone.querySelectorAll<HTMLElement>(':scope > .nav-draggable').forEach(dr => {
      const k = dr.dataset['kind'];
      if (k === 'file' && dr.dataset['path']) {
        const base = dr.dataset['path'].split('/').pop()!;
        entries.push({ kind: 'file', path: `${parent}/${base}` });
      } else if (k === 'dir' && dr.dataset['dir']) {
        entries.push({ kind: 'dir', dir: dr.dataset['dir'] });
      }
    });
    order[parent] = entries;
  });
  return prev.icons ? { version: 2, order, icons: { ...prev.icons } } : { version: 2, order };
}

